import * as React from "react";
import { useMemo } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { LayoutGrid, Table } from "lucide-react";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import TableTemplate from "../common/TableTemplate";
import { columnHelper, safeNumberConversion } from "../../utils/utils";

const ProductList = ({ allProducts }) => {
  const { totalSalesRevenue, totalProfit, totalAdSpend } = useMemo(() => {
    if (!allProducts?.data) {
      return { totalSalesRevenue: 0, totalProfit: 0, totalAdSpend: 0 };
    }

    const totalSalesRevenue = Number(
      allProducts.data
        .reduce((acc, product) => {
          const value = safeNumberConversion(product?.sales_revenue?.display);
          return acc + value;
        }, 0)
        .toFixed(2)
    );

    const totalProfit = Number(
      allProducts.data
        .reduce((acc, product) => {
          const value = safeNumberConversion(product?.profit?.display);
          return acc + value;
        }, 0)
        .toFixed(2)
    );

    const totalAdSpend = Number(
      allProducts.data
        .reduce((acc, product) => {
          const value = safeNumberConversion(product?.ad_spend?.display);
          return acc + value;
        }, 0)
        .toFixed(2)
    );

    return { totalSalesRevenue, totalProfit, totalAdSpend };
  }, [allProducts]);

  const columns = useMemo(
    () =>
      generateDynamicColumns({
        columnHelper,
        headerData: allProducts.Column_header,
        totals: {
          sales_revenue: totalSalesRevenue,
          profit: totalProfit,
          ad_spend: totalAdSpend,
        },
        navigationConfig: {
          name: {
            navigable: true,
            getUrl: ({ originalRow }) => {
              const glid = originalRow["glid"].display;
              return {
                pathname: `/product/${glid}`,
                state: null,
              };
            },
          },
          ali_link: {
            navigable: true,
            target: "_blank",
            getUrl: ({ originalRow, columnInfo }) => {
              return {
                pathname: originalRow?.[columnInfo.key]?.display,
                state: null,
              };
            },
          },
          uri: {
            navigable: true,
            target: "_blank",
            getUrl: ({ originalRow, columnInfo }) => {
              return {
                pathname: originalRow?.[columnInfo.key]?.display,
                state: null,
              };
            },
          },
          shop_name: {
            navigable: true,
            target: "_blank",
            getUrl: ({ originalRow, columnInfo }) => {
              return {
                pathname: originalRow?.[columnInfo.key]?.display,
                state: null,
              };
            },
          },
        },
        filterConfig: {
          filterTypes: {
            status: "select",
            missing_cost: "select",
            is_addon: "select",
          },
          excludeFromFilter: ["actions", "rowSelection", "uri", "ali_link"],
        },
        editableFields: {
          status: {
            inputType: "select",
          },
          is_addon: {
            inputType: "select",
          },
        },
      }),
    [allProducts, totalSalesRevenue, totalProfit, totalAdSpend]
  );

  // it's fine to not put this object inside useMemo for memoization
  // and I have forgotten why I wrote the above comment, classic coming back to the code after a while moment right here
  const tableOptions = {
    enableGlobalFilter: true,
    enablePagination: true,
    enableColumnVisibility: true,
    enableSorting: true,
    enableColumnFilters: true,
    enableColumnOrder: true,
    enableColumnResize: true,
    enableRowSelection: true, // Enable row selection
    defaultPageSize: 50,
    showTotalItems: true,
    showPreferences: true,
  };

  return (
    <div className="space-y-4 w-full min-w-0">
      <Tabs defaultValue="table" className="w-full">
        <div className="flex items-center justify-between mb-6">
          <TabsList>
            <TabsTrigger value="table" className="flex items-center gap-2">
              <Table className="h-4 w-4" />
              Table View
            </TabsTrigger>
            <TabsTrigger value="card" className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4" />
              Card View
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="table">
          <div className="w-full">
              <TableTemplate
                columns={columns || []}
                data={allProducts.data || []}
                options={{
                  ...tableOptions,
                  enableRowSelection: allProducts.Column_header.row_checkbox,
                }}
                layout="standard"
              />
          </div>
        </TabsContent>

        <TabsContent value="card">
          <TableTemplate
            columns={columns || []}
            data={allProducts.data || []}
            options={{
              ...tableOptions,
              defaultPageSize: 51,
              enableRowSelection: allProducts.Column_header.row_checkbox,
            }}
            layout="card"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductList;
